/**
 * FoodPulseTopicLanding — reusable landing page for any dropdown-item
 * article. Renders:
 *   1. Hero (section colour, topic label, blurb)
 *   2. Article body (from food_articles by slug)
 *   3. Sibling topic cards for cross-navigation
 *   4. Primary CTA that links to the actual feature the topic maps to
 *      (deals grid, businesses directory, community forums, etc.)
 *
 * Routes:
 *   /food-pulse/community/:topic
 *   /food-pulse/deals-guide/:topic
 *   /food-pulse/news/:topic
 *
 * The manifest below drives everything — one config change adds a new
 * section without touching JSX.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import VolunteerSignupForm from "@/components/foodpulse/VolunteerSignupForm";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Section manifests ────────────────────────────────────────────────
// Each entry: { slug, label, blurb, cta, topics: [{slug,label,blurb}] }
// `slug` == food_articles.category.  Topic `slug` == food_articles.tag.
// Article slug convention: `<section>-<topic>` (or `<section>-all` for hub).
const SECTIONS = {
  community: {
    label:  "Community",
    accent: "#8B5E10",
    surface: "#F0E2C7",
    ink: "#4A2E0F",
    blurb: "Where food brings people together.",
    slugPrefix: "community",
    hubRoute: "/food-pulse/community",
    articleSlug: (topic) => `community-${topic}`,
    topics: [
      { slug: "all",            label: "All of Community",  cta: null,                                blurb: "Cooking advice, reviews, volunteers, charities and events." },
      { slug: "forums",         label: "Discussion Forums", cta: { to: "/food-pulse/community/forums",       label: "Browse discussions →" }, blurb: "Cooking, cuisines, farming, tech and safety." },
      { slug: "q-and-a",        label: "Questions & Answers", cta: null,                                     blurb: "Reliable answers from the community." },
      { slug: "reviews",        label: "Food Reviews",      cta: { to: "/food-pulse/businesses",              label: "Browse listings →" }, blurb: "Honest reviews from real people." },
      { slug: "volunteers",     label: "Volunteers",        cta: { to: "/food-pulse/articles?tag=volunteer",  label: "Volunteer near you →" }, blurb: "Fight hunger in your community." },
      { slug: "assistance",     label: "Food Assistance",   cta: { to: "/food-pulse/businesses?type=food_bank,food_pantry,food_charity,soup_kitchen", label: "Find food assistance →" }, blurb: "Support for families facing food insecurity." },
      { slug: "charities",      label: "Food Charities",    cta: { to: "/food-pulse/businesses?type=food_bank,food_pantry,food_charity,soup_kitchen", label: "Discover charities →" }, blurb: "Organizations feeding communities." },
      { slug: "local-groups",   label: "Local Groups",      cta: null,                                        blurb: "Food lovers near you." },
      { slug: "events",         label: "Events",            cta: { to: "/food-pulse/events",                  label: "See what's on →" }, blurb: "Festivals, markets, tastings and more." },
    ],
  },
  "deals-guide": {
    label:  "Deals Guide",
    accent: "#B25D38",
    surface: "#FFE9D0",
    ink: "#5A2600",
    blurb: "Save more with FoodPulse Deals.",
    slugPrefix: "deals-guide",
    hubRoute: "/food-pulse/deals-guide",
    articleSlug: (topic) => `deals-guide-${topic}`,
    topics: [
      { slug: "all",          label: "All of Deals",     cta: { to: "/food-pulse/deals", label: "Browse today's deals →" }, blurb: "Groceries, restaurants, coupons and more." },
      { slug: "grocery",      label: "Grocery Deals",    cta: { to: "/food-pulse/deals?kind=surplus", label: "Grocery deals →" }, blurb: "Best savings on everyday essentials." },
      { slug: "restaurant",   label: "Restaurant Specials", cta: { to: "/food-pulse/deals?kind=offer", label: "Restaurant specials →" }, blurb: "Lunch, happy hour, family meals." },
      { slug: "coupons",      label: "Coupons",          cta: { to: "/food-pulse/deals?kind=coupon", label: "Grab a coupon →" }, blurb: "Digital coupons and promo codes." },
      { slug: "clearance",    label: "Clearance Items",  cta: { to: "/food-pulse/deals?kind=surplus", label: "See clearance →" }, blurb: "Last-chance bargains." },
      { slug: "flash-sales",  label: "Flash Sales",      cta: { to: "/food-pulse/deals", label: "Catch a flash sale →" }, blurb: "Short-window exceptional discounts." },
      { slug: "cashback",     label: "Cashback",         cta: null,                                          blurb: "Money back on eligible purchases." },
      { slug: "loyalty",      label: "Loyalty Programs", cta: null,                                          blurb: "Rewards for being a regular." },
    ],
  },
  news: {
    label:  "News",
    accent: "#0F4C81",
    surface: "#DDECFA",
    ink: "#0F2E4E",
    blurb: "The latest from the global food ecosystem.",
    slugPrefix: "news",
    hubRoute: "/food-pulse/news",
    articleSlug: (topic) => `news-${topic}`,
    topics: [
      { slug: "all",                label: "All of News",           cta: { to: "/food-pulse/articles?category=news", label: "Browse all news →" }, blurb: "Breaking headlines from around the world." },
      { slug: "food-industry",      label: "Food Industry",         cta: { to: "/food-pulse/articles?category=news&q=industry", label: "Industry news →" }, blurb: "Manufacturing, retail and market analysis." },
      { slug: "agriculture",        label: "Agriculture",           cta: { to: "/food-pulse/articles?category=news&q=agriculture", label: "Farm news →" }, blurb: "Farms, crops and farming tech." },
      { slug: "nutrition-research", label: "Nutrition Research",    cta: { to: "/food-pulse/articles?category=news&q=nutrition", label: "Nutrition studies →" }, blurb: "The science of healthy eating." },
      { slug: "restaurant",         label: "Restaurant News",       cta: { to: "/food-pulse/articles?category=news&q=restaurant", label: "Restaurant world →" }, blurb: "Openings, chefs and menu innovations." },
      { slug: "regulations",        label: "Government Regulations",cta: { to: "/food-pulse/articles?category=news&q=regulation", label: "Regulatory news →" }, blurb: "Food laws that protect consumers." },
      { slug: "recalls",            label: "Product Recalls",       cta: { to: "/food-pulse/articles?category=news&q=recall", label: "Recent recalls →" }, blurb: "Timely food safety alerts." },
      { slug: "sustainability",     label: "Sustainability",        cta: { to: "/food-pulse/articles?category=news&q=sustain", label: "Sustainability news →" }, blurb: "Building a better food future." },
      { slug: "technology",         label: "Technology",            cta: { to: "/food-pulse/articles?category=news&q=technology", label: "Food tech →" }, blurb: "AI, robotics and traceability." },
    ],
  },
  "food-waste": {
    label:  "Food Waste",
    accent: "#1F7A57",
    surface: "#E7F5EC",
    ink: "#0F3D2E",
    blurb: "Rescue food. Protect our planet.",
    slugPrefix: "food-waste",
    hubRoute: "/food-pulse/food-waste",
    articleSlug: (topic) => `food-waste-${topic === "recovery-organizations" ? "recovery-organizations" : topic}`,
    topics: [
      { slug: "all",                     label: "All of Food Waste",         cta: null, blurb: "Rescue, donation, composting and recovery in one place." },
      { slug: "food-rescue",             label: "Food Rescue",               cta: { to: "/food-pulse/businesses?type=food_bank,food_pantry,food_charity,soup_kitchen", label: "Find rescue organizations →" }, blurb: "Surplus food connected to people who need it." },
      { slug: "surplus-food",            label: "Surplus Food",              cta: { to: "/food-pulse/deals?kind=surplus", label: "Browse surplus deals →" }, blurb: "Extra food given a second chance." },
      { slug: "donate-food",             label: "Donate Food",               cta: { to: "/food-pulse/businesses?type=food_bank,food_pantry,food_charity,soup_kitchen", label: "Find donation centres →" }, blurb: "Where and how to give safely." },
      { slug: "composting",              label: "Composting",                cta: null, blurb: "Turn scraps into healthy soil." },
      { slug: "zero-waste-tips",         label: "Zero Waste Tips",           cta: null, blurb: "Everyday habits that add up." },
      { slug: "leftover-recipes",        label: "Leftover Recipes",          cta: { to: "/food-pulse/recipes", label: "Recipe library →" }, blurb: "Delicious meals from yesterday's food." },
      { slug: "sustainability",          label: "Sustainability",            cta: null, blurb: "A more resilient food future." },
      { slug: "recovery-organizations",  label: "Food Recovery Organizations", cta: { to: "/food-pulse/businesses?type=food_bank,food_pantry,food_charity,soup_kitchen", label: "Directory →" }, blurb: "The non-profits fighting food waste." },
    ],
  },
  discover: {
    label:  "Discover",
    accent: "#B25D38",
    surface: "#FFF3E0",
    ink: "#4A2E0F",
    blurb: "Find great food near you.",
    slugPrefix: "discover",
    hubRoute: "/food-pulse/discover",
    articleSlug: (topic) => `discover-${topic}`,
    topics: [
      { slug: "all",                label: "All of Discover",  cta: { to: "/food-pulse/businesses", label: "Browse all places →" }, blurb: "Every restaurant, market and food place." },
      { slug: "restaurants",        label: "Restaurants",      cta: { to: "/food-pulse/businesses?type=restaurant,fast_food,food_court", label: "Find a restaurant →" }, blurb: "Fine dining to family favourites." },
      { slug: "cafes-coffee",       label: "Cafés & Coffee",   cta: { to: "/food-pulse/businesses?type=cafe,coffee_shop", label: "Find a café →" }, blurb: "Specialty roasters and espresso bars." },
      { slug: "bakeries",           label: "Bakeries",         cta: { to: "/food-pulse/businesses?type=bakery,pastry", label: "Find a bakery →" }, blurb: "Bread, cakes and pastries baked fresh." },
      { slug: "farmers-markets",    label: "Farmers Markets",  cta: { to: "/food-pulse/businesses?type=farmers_market,csa", label: "Find a market →" }, blurb: "Fresh food direct from local farmers." },
      { slug: "grocery-stores",     label: "Grocery Stores",   cta: { to: "/food-pulse/businesses?type=supermarket,grocery,convenience_store,health_food", label: "Find a grocer →" }, blurb: "Shop smarter for everyday essentials." },
      { slug: "food-trucks",        label: "Food Trucks",      cta: { to: "/food-pulse/businesses?type=food_truck", label: "Find a food truck →" }, blurb: "Great food on the move." },
      { slug: "local-producers",    label: "Local Producers",  cta: { to: "/food-pulse/businesses?type=farm,csa,caterer", label: "Find local producers →" }, blurb: "Farmers, ranchers and small makers." },
      { slug: "specialty-stores",   label: "Specialty Stores", cta: { to: "/food-pulse/businesses?type=butcher,seafood_shop,cheese_shop,chocolate_shop,confectionery,spice_shop,tea_shop", label: "Find specialty shops →" }, blurb: "Gourmet and international foods." },
      { slug: "food-festivals",     label: "Food Festivals",   cta: { to: "/food-pulse/festivals", label: "Festival calendar →" }, blurb: "Celebrate food, culture and community." },
    ],
  },
  farming: {
    label:  "Farming",
    accent: "#2F6B2F",
    surface: "#DCEAD1",
    ink: "#1A3B1A",
    blurb: "Connecting the world through agriculture.",
    slugPrefix: "farming",
    hubRoute: "/food-pulse/farming",
    articleSlug: (topic) => `farming-${topic}`,
    topics: [
      { slug: "all",                   label: "All of Farming",          cta: null, blurb: "Farmers, farms, markets and climate resilience." },
      { slug: "farmers",               label: "Farmers",                 cta: null, blurb: "The people who feed the world." },
      { slug: "farms-ranches",         label: "Farms & Ranches",         cta: { to: "/food-pulse/businesses?type=farm,csa", label: "Find a farm →" }, blurb: "Vegetable farms, orchards, ranches and more." },
      { slug: "farmers-markets",       label: "Farmers Markets",         cta: { to: "/food-pulse/businesses?type=farmers_market,csa", label: "Find a market →" }, blurb: "Fresh food, direct from growers." },
      { slug: "home-garden-registry",  label: "Home Garden Registry",    cta: null, blurb: "Register your plot and track harvests." },
      { slug: "regenerative",          label: "Regenerative Agriculture",cta: null, blurb: "Restoring soil, biodiversity and water." },
      { slug: "climate",               label: "Climate & Agriculture",   cta: { to: "/food-pulse/articles?category=news&q=climate", label: "Climate news →" }, blurb: "Farming in a changing climate." },
    ],
  },
  marketplace: {
    label:  "Marketplace",
    accent: "#7C3AED",
    surface: "#EFE7FF",
    ink: "#37206B",
    blurb: "Buyers, sellers, producers and service providers.",
    slugPrefix: "marketplace",
    hubRoute: "/food-pulse/marketplace",
    articleSlug: (topic) => `marketplace-${topic}`,
    topics: [
      { slug: "all",              label: "All of Marketplace",  cta: null,                                                                                     blurb: "One place for every food listing." },
      { slug: "buy-sell",         label: "Buy & Sell",          cta: null,                                                                                     blurb: "Products, ingredients and kitchen supplies." },
      { slug: "farmers",          label: "Farmers",             cta: { to: "/food-pulse/businesses?type=farm,csa",                           label: "Find a farmer →" }, blurb: "Fresh produce direct from local farmers." },
      { slug: "home-bakers",      label: "Home Bakers",         cta: { to: "/food-pulse/businesses?type=bakery,pastry",                      label: "Find a baker →" }, blurb: "Homemade breads, cakes and desserts." },
      { slug: "catering",         label: "Catering",            cta: { to: "/food-pulse/businesses?type=caterer",                            label: "Find a caterer →" }, blurb: "Catering for every occasion." },
      { slug: "kitchen-equipment",label: "Kitchen Equipment",   cta: null,                                                                                     blurb: "For every commercial and home kitchen." },
      { slug: "classifieds",      label: "Classifieds",         cta: null,                                                                                     blurb: "Rentals, equipment and business opportunities." },
      { slug: "jobs",             label: "Jobs",                cta: null,                                                                                     blurb: "Careers across the food industry." },
      { slug: "services",         label: "Services",            cta: null,                                                                                     blurb: "Photography, packaging, marketing and more." },
    ],
  },
  places: {
    label:  "Places",
    accent: "#0F766E",
    surface: "#DFF3EE",
    ink: "#0D3B37",
    blurb: "Food connected to geography.",
    slugPrefix: "places",
    hubRoute: "/food-pulse/places",
    articleSlug: (topic) => `places-${topic}`,
    topics: [
      { slug: "all",               label: "All of Places",       cta: { to: "/food-pulse/businesses",                                               label: "Browse the map →" }, blurb: "Every food place, every geography." },
      { slug: "countries",         label: "Countries",           cta: null,                                                                                 blurb: "National dishes and food cultures." },
      { slug: "states",            label: "States",              cta: null,                                                                                 blurb: "Regional food, state by state." },
      { slug: "cities",            label: "Cities",              cta: { to: "/food-pulse/businesses",                                               label: "Browse city listings →" }, blurb: "Complete city food guides." },
      { slug: "neighborhoods",     label: "Neighborhoods",       cta: null,                                                                                 blurb: "Food close to home." },
      { slug: "restaurants",       label: "Restaurants",         cta: { to: "/food-pulse/businesses?type=restaurant,fast_food,food_court",           label: "Find a restaurant →" }, blurb: "Dining destinations worldwide." },
      { slug: "grocery-stores",    label: "Grocery Stores",      cta: { to: "/food-pulse/businesses?type=supermarket,grocery,convenience_store,health_food", label: "Find a grocer →" }, blurb: "Everyday essentials, anywhere." },
      { slug: "farms",             label: "Farms",               cta: { to: "/food-pulse/businesses?type=farm,csa",                                 label: "Find a farm →" }, blurb: "Agritourism, orchards and ranches." },
      { slug: "vineyards",         label: "Vineyards",           cta: { to: "/food-pulse/businesses?type=winery,vineyard,brewery",                  label: "Find a vineyard →" }, blurb: "Wine regions and tasting rooms." },
      { slug: "markets",           label: "Markets",             cta: { to: "/food-pulse/businesses?type=farmers_market,csa",                       label: "Find a market →" }, blurb: "Fresh, local, seasonal." },
      { slug: "parks-attractions", label: "Parks & Attractions", cta: null,                                                                                 blurb: "Food meets travel and nature." },
    ],
  },
};

export default function FoodPulseTopicLanding({ section }) {
  const { topic: topicParam } = useParams();
  const cfg = SECTIONS[section];

  const topic = useMemo(() => {
    if (!cfg) return null;
    const t = topicParam || "all";
    return cfg.topics.find((x) => x.slug === t) || cfg.topics[0];
  }, [topicParam, cfg]);

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cfg || !topic) return;
    let cancelled = false;
    setLoading(true);
    setArticle(null);
    document.title = `${topic.label} — Food Pulse`;
    (async () => {
      try {
        const slug = cfg.articleSlug(topic.slug);
        const res = await axios.get(`${API}/api/food-articles/${slug}`);
        if (!cancelled) setArticle(res.data);
      } catch {
        if (!cancelled) setArticle(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [topic, cfg]);

  if (!cfg || !topic) return null;

  const isHub = topic.slug === "all";
  const others = cfg.topics.filter((t) => t.slug !== topic.slug && t.slug !== "all");

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }}
         data-testid={`fp-${section}-${topic.slug}`}>
      <SeoHead section="food-pulse"
               title={`${topic.label} — Food Pulse ${cfg.label}`}
               description={article?.excerpt || topic.blurb} />

      {/* Hero */}
      <section className="border-b" style={{ background: cfg.surface, borderColor: "#E9D8B4" }}>
        <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
          <Link to={isHub ? "/food-pulse" : cfg.hubRoute}
                className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest mb-3 hover:underline"
                style={{ color: cfg.accent }}
                data-testid="topic-back">
            <ArrowLeft className="w-3 h-3" />
            {isHub ? "Food Pulse" : `Food Pulse · ${cfg.label}`}
          </Link>
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: cfg.accent }}>
            Food Pulse · {cfg.label}
          </div>
          <h1 className="font-display text-3xl md:text-5xl leading-tight mt-1"
              style={{ color: cfg.ink }} data-testid="topic-title">
            {topic.label}
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base"
             style={{ color: cfg.ink, opacity: 0.85 }}>
            {topic.blurb}
          </p>
          {topic.cta && (
            <Link to={topic.cta.to}
                  className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white hover:shadow-md transition-shadow"
                  style={{ background: cfg.accent }}
                  data-testid="topic-cta">
              {topic.cta.label}
            </Link>
          )}
        </div>
      </section>

      {/* Body — intro paragraph appears immediately above the sibling grid;
          any richer content continues below the grid so readers see both
          the article summary and the "More on X" tabs at a glance. */}
      <section className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-sm opacity-70" data-testid="topic-loading">Loading article…</div>
        ) : !article ? (
          <div className="rounded-2xl border p-10 text-center bg-white"
               style={{ borderColor: "#C79A3F" }}
               data-testid="topic-empty">
            <div className="font-display text-lg mb-1">Introduction coming soon.</div>
            <p className="text-sm opacity-70">
              We're preparing this topic. In the meantime, browse the other {cfg.label} pages below.
            </p>
          </div>
        ) : (
          <TopicArticleIntro html={article.body_html} />
        )}

        {section === "community" && topic.slug === "volunteers" && (
          <div className="mt-10 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-dashed p-4 sm:p-5"
                 style={{ borderColor: cfg.accent, background: cfg.surface }}
                 data-testid="volunteer-browse-cta">
              <div className="flex-1 min-w-[220px]">
                <div className="font-display text-lg" style={{ color: cfg.ink }}>
                  Already ready to help?
                </div>
                <div className="text-xs opacity-80">
                  Browse every open volunteer opportunity across food, housing, education
                  and disaster relief — powered by Social Pulse.
                </div>
              </div>
              <Link to="/opportunities"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-semibold hover:shadow-md transition-shadow whitespace-nowrap"
                    style={{ background: cfg.accent }}
                    data-testid="volunteer-browse-opportunities">
                Browse open opportunities →
              </Link>
            </div>
            <VolunteerSignupForm
              siteOrigin="foodpulse"
              heading="Join the FoodPulse Volunteer Network"
              subheading="One form. Every food-related volunteer opportunity in your city."
              browseOpportunitiesHref="/opportunities"
            />
          </div>
        )}
      </section>

      {/* Sibling grid — mid-page pivot so readers see other topics
          without scrolling past the whole article first. */}
      <section className="max-w-4xl mx-auto px-4 pb-6"
               data-testid="topic-siblings">
        <div className="text-[10px] font-mono uppercase tracking-widest mb-3"
             style={{ color: cfg.accent }}>
          — More on {cfg.label}
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(isHub ? cfg.topics.slice(1) : others).map((t) => (
            <Link key={t.slug}
                  to={`${cfg.hubRoute}/${t.slug}`}
                  className="p-4 rounded-lg border hover:shadow-md transition-all hover:-translate-y-0.5 bg-white group"
                  style={{ borderColor: "#E9D8B4" }}
                  data-testid={`topic-sibling-${t.slug}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-display text-base" style={{ color: "#1A1613" }}>
                  {t.label}
                </span>
                <ArrowRight className="w-4 h-4 opacity-30 group-hover:opacity-100 transition-opacity"
                            style={{ color: cfg.accent }} />
              </div>
              <div className="text-xs opacity-70">{t.blurb}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Continuation of the article — everything past the intro */}
      {article && <TopicArticleContinuation html={article.body_html} />}
    </div>
  );
}


// ── Article splitters ────────────────────────────────────────────────
/**
 * Split an article's body_html into "intro" (first 2 paragraphs / first
 * `<h2>` boundary) and "continuation" (everything after). The split
 * respects HTML block boundaries so we never break a tag in half.
 */
function splitBody(html) {
  if (!html) return { intro: "", rest: "" };
  const h2Match = html.match(/<h2[\s>]/i);
  if (h2Match && h2Match.index > 0) {
    return { intro: html.slice(0, h2Match.index).trim(),
             rest:  html.slice(h2Match.index).trim() };
  }
  // Fall back to first two paragraphs
  const paras = [];
  const re = /<p[\s>][\s\S]*?<\/p>/gi;
  let m;
  while ((m = re.exec(html)) && paras.length < 2) paras.push(m);
  if (paras.length && paras[paras.length - 1]) {
    const end = paras[paras.length - 1].index + paras[paras.length - 1][0].length;
    return { intro: html.slice(0, end).trim(),
             rest:  html.slice(end).trim() };
  }
  return { intro: html, rest: "" };
}


function TopicArticleIntro({ html }) {
  const { intro } = splitBody(html);
  return (
    <article className="prose prose-lg max-w-none"
             style={{ color: "#3E2A0A" }}
             data-testid="topic-body"
             dangerouslySetInnerHTML={{ __html: intro || html }} />
  );
}


function TopicArticleContinuation({ html }) {
  const { rest } = splitBody(html);
  if (!rest) return null;
  return (
    <section className="max-w-4xl mx-auto px-4 pb-16"
             data-testid="topic-body-continuation">
      <article className="prose prose-lg max-w-none"
               style={{ color: "#3E2A0A" }}
               dangerouslySetInnerHTML={{ __html: rest }} />
    </section>
  );
}
